import {actionTool, objectInput, readOnlyTool, stringInput, waitForSecrets} from "@/functions/webMcpTool";
import {chooseOption, cleanText, findButtonByText, isVisible, labelFor, press, pressButtonWithText, setChecked} from "@/functions/webMcpDom";
import getBrandName from "@/functions/getBrandName";

const METHOD_OPTIONS = {
	authenticator_app: '#totpOption',
	email: '#emailOption',
	text: '#textOption',
	voice_call: '#voiceOption',
}

const availableMethods = () => {
	const rendered = Object.keys(METHOD_OPTIONS).filter((method) => document.querySelector(METHOD_OPTIONS[method]))
	return rendered.length ? rendered : Object.keys(METHOD_OPTIONS)
}

const phoneLabel = (radio) => labelFor(radio)

const phoneOptions = () => [...document.querySelectorAll('input[name="selectContactInfo"]')]
	.filter((radio) => !radio.closest('.d-none') && isVisible(radio))

const digitsOf = (value) => String(value ?? '').replace(/\D/g, '')

const findPhoneOption = (phone) => {
	const wanted = String(phone).trim().toLowerCase()
	const wantedDigits = digitsOf(phone)
	return phoneOptions().find((radio) => {
		const label = phoneLabel(radio).toLowerCase()
		return label === wanted || (wantedDigits.length >= 2 && digitsOf(label).endsWith(wantedDigits))
	})
}

const buildChooseVerificationMethod = () => actionTool({
	name: 'choose_verification_method',
	description: `Choose how ${getBrandName()} sends the person's two-step verification code, then continue. Only the methods set up on this account are offered. When the account has several phone numbers, pass the masked number to use; if it is missing, the tool returns the numbers to choose from. The next step asks for the code.`,
	inputSchema: objectInput({
		method: stringInput('How the person wants to receive the code.', {enum: availableMethods()}),
		phone: stringInput('Masked phone number to use when there are several, exactly as listed, for example "(***) ***-1234".'),
	}, ['method']),
	run: ({method, phone}) => {
		const selector = METHOD_OPTIONS[method]
		if (!selector) throw new Error(`Unknown verification method "${method}".`)
		chooseOption(selector, `${method.replace('_', ' ')} option`)
		if (['text', 'voice_call'].includes(method) && phoneOptions().length > 1) {
			const option = phone ? findPhoneOption(phone) : null
			if (!option) {
				return {result: {outcome: 'phone_needed', note: 'Call choose_verification_method again with one of these phone numbers.', phones: phoneOptions().map(phoneLabel)}}
			}
			chooseOption(`input[name="selectContactInfo"][id=${JSON.stringify(option.id)}]`, 'phone number option')
		}
		pressButtonWithText('Continue')
	},
})

const buildUseEmergencyCode = () => (findButtonByText('Use Emergency Code Verification', document.body) ? actionTool({
	name: 'use_emergency_code',
	description: 'Verify with one of the person\'s saved emergency codes instead of a code sent by email, text, or call. The next step asks for the emergency code, which only the person has saved and types into the page themselves. Each emergency code works once. Only offered when the account has emergency codes turned on.',
	run: () => pressButtonWithText('Use Emergency Code Verification'),
}) : [])

const buildSubmitVerificationCode = () => actionTool({
	name: 'submit_verification_code',
	description: `Continue signing in to ${getBrandName()} once the two-step verification code is in the page. The person types the code, or the browser fills it from the text message; it never passes through this tool, so it waits until the field is filled. Pass rememberDevice to choose whether this device is remembered. Returns the next step: the tax return, a required account change, or a message if the code was wrong.`,
	inputSchema: objectInput({
		rememberDevice: {type: 'boolean', description: 'Whether to remember this device so it skips two-step verification next time.'},
	}),
	run: ({rememberDevice}) => {
		const waiting = waitForSecrets('submit_verification_code', ['#twoFactorCode'])
		if (waiting) return waiting
		if (typeof rememberDevice === 'boolean' && document.querySelector('#rememberMe')) {
			setChecked('#rememberMe', rememberDevice, 'Remember this device checkbox')
		}
		return press('button[type="submit"][form="verificationForm"]', 'Continue button')
	},
})

const helpOptionButtons = () => [...document.querySelectorAll('#helpTwoStepModal .modal-body button')]

const helpOptions = () => helpOptionButtons().map(cleanText).filter(Boolean)

const buildGetHelpOptions = () => readOnlyTool({
	name: 'get_verification_help_options',
	description: 'List the other ways to get or use a verification code that this page offers, such as sending a new code, calling instead of texting, a different phone number, the authenticator app, email, or an emergency code. Also lists help for a person who can\'t reach their phone or email. Use request_verification_help to pick one.',
	execute: () => ({options: helpOptions()}),
})

const buildRequestHelp = () => {
	const options = helpOptions()
	return actionTool({
		name: 'request_verification_help',
		description: 'Pick one of the page\'s verification help options, exactly as get_verification_help_options lists it: send a new code, switch between text and call, use another phone number, the authenticator app, email, or an emergency code. The options about losing access open a help article instead.',
		inputSchema: objectInput({
			option: stringInput('The help option, exactly as listed by get_verification_help_options.', options.length ? {enum: options} : {}),
		}, ['option']),
		run: ({option}) => {
			const wanted = String(option ?? '').trim().toLowerCase()
			if (!helpOptions().some((text) => text.toLowerCase() === wanted)) {
				return {result: {error: `"${option}" is not a help option on this page. Call get_verification_help_options for the list.`}}
			}
			pressButtonWithText('Need help?')
			const button = helpOptionButtons().find((element) => cleanText(element).toLowerCase() === wanted)
			button.click()
		},
	})
}

const codeEntry = [buildSubmitVerificationCode, buildGetHelpOptions, buildRequestHelp]

export const twoStepTools = {
	VerifyWithTwoStepMethod: [buildChooseVerificationMethod, buildUseEmergencyCode],
	EmailVerification: codeEntry,
	TextVerification: codeEntry,
	AuthAppVerification: codeEntry,
	EmergencyCodeVerification: codeEntry,
}